const CACHE_PREFIX = 'agent_cache_';
const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

class CacheService {
  constructor() {
    this.cache = new Map();
    this.cacheTimestamps = new Map();
  }

  /**
   * Generate cache key from endpoint and parameters
   * @param {string} endpoint - API endpoint
   * @param {object} params - API parameters
   * @returns {string} - Cache key
   */
  generateCacheKey(endpoint, params = {}) {
    const paramString = JSON.stringify(params);
    return `${endpoint}_${paramString}`;
  }

  /**
   * Set item in cache with optional TTL
   * @param {string} key - Cache key
   * @param {any} value - Value to cache
   * @param {number} ttl - Time to live in milliseconds (default: 5 minutes)
   */
  set(key, value, ttl = DEFAULT_TTL) {
    const cacheKey = CACHE_PREFIX + key;
    const item = {
      value,
      timestamp: Date.now(),
      ttl
    };

    // Store in memory
    this.cache.set(cacheKey, item);
    this.cacheTimestamps.set(cacheKey, Date.now());

    // Store in sessionStorage (prefer sessionStorage for API data)
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(item));
    } catch (error) {
      console.warn('Failed to cache in sessionStorage:', error);
      // Try localStorage as fallback
      try {
        localStorage.setItem(cacheKey, JSON.stringify(item));
      } catch (localError) {
        console.warn('Failed to cache in localStorage:', error);
      }
    }
  }

  /**
   * Get item from cache
   * @param {string} key - Cache key
   * @returns {any|null} - Cached value or null if not found/expired
   */
  get(key) {
    const cacheKey = CACHE_PREFIX + key;

    // Try memory cache first (fastest)
    let item = this.cache.get(cacheKey);

    // If not in memory, try sessionStorage
    if (!item) {
      try {
        const stored = sessionStorage.getItem(cacheKey);
        if (stored) {
          item = JSON.parse(stored);
          // Restore to memory cache
          this.cache.set(cacheKey, item);
          this.cacheTimestamps.set(cacheKey, item.timestamp);
        }
      } catch (error) {
        console.warn('Failed to read from sessionStorage:', error);
        // Try localStorage as fallback
        try {
          const stored = localStorage.getItem(cacheKey);
          if (stored) {
            item = JSON.parse(stored);
            this.cache.set(cacheKey, item);
            this.cacheTimestamps.set(cacheKey, item.timestamp);
          }
        } catch (localError) {
          console.warn('Failed to read from localStorage:', error);
          return null;
        }
      }
    }

    // Check if item exists and is not expired
    if (item) {
      const now = Date.now();
      const age = now - item.timestamp;

      if (age < item.ttl) {
        return item.value;
      } else {
        // Remove expired item
        this.remove(key);
      }
    }

    return null;
  }

  /**
   * Remove item from cache
   * @param {string} key - Cache key
   */
  remove(key) {
    const cacheKey = CACHE_PREFIX + key;

    // Remove from memory
    this.cache.delete(cacheKey);
    this.cacheTimestamps.delete(cacheKey);

    // Remove from sessionStorage
    try {
      sessionStorage.removeItem(cacheKey);
    } catch (error) {
      console.warn('Failed to remove from sessionStorage:', error);
    }

    // Remove from localStorage
    try {
      localStorage.removeItem(cacheKey);
    } catch (error) {
      console.warn('Failed to remove from localStorage:', error);
    }
  }

  /**
   * Clear all cache entries
   */
  clear() {
    // Clear memory cache
    this.cache.clear();
    this.cacheTimestamps.clear();

    // Clear sessionStorage cache entries
    try {
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(CACHE_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => sessionStorage.removeItem(key));
    } catch (error) {
      console.warn('Failed to clear sessionStorage cache:', error);
    }

    // Clear localStorage cache entries
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(CACHE_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.warn('Failed to clear localStorage cache:', error);
    }
  }

  /**
   * Check if a key exists and is not expired
   * @param {string} key - Cache key
   * @returns {boolean}
   */
  has(key) {
    return this.get(key) !== null;
  }

  /**
   * Get cache statistics
   * @returns {object} - Cache statistics
   */
  getStats() {
    const memoryKeys = Array.from(this.cache.keys());
    const sessionStorageKeys = [];
    const localStorageKeys = [];

    try {
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(CACHE_PREFIX)) {
          sessionStorageKeys.push(key);
        }
      }
    } catch (error) {
      console.warn('Failed to get sessionStorage stats:', error);
    }

    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(CACHE_PREFIX)) {
          localStorageKeys.push(key);
        }
      }
    } catch (error) {
      console.warn('Failed to get localStorage stats:', error);
    }

    return {
      memoryEntries: memoryKeys.length,
      sessionStorageEntries: sessionStorageKeys.length,
      localStorageEntries: localStorageKeys.length,
      memoryKeys: memoryKeys.map(k => k.replace(CACHE_PREFIX, '')),
      sessionStorageKeys: sessionStorageKeys.map(k => k.replace(CACHE_PREFIX, '')),
      localStorageKeys: localStorageKeys.map(k => k.replace(CACHE_PREFIX, ''))
    };
  }

  /**
   * Remove expired entries from cache
   */
  cleanup() {
    const now = Date.now();
    const keysToRemove = [];

    // Check memory cache
    this.cache.forEach((item, key) => {
      const age = now - item.timestamp;
      if (age >= item.ttl) {
        keysToRemove.push(key.replace(CACHE_PREFIX, ''));
      }
    });

    // Remove expired entries
    keysToRemove.forEach(key => this.remove(key));

    console.log(`Cache cleanup: removed ${keysToRemove.length} expired entries`);
  }
}

// Export singleton instance
export const cacheService = new CacheService();

// Export cache keys constants
export const CACHE_KEYS = {
  PARENT_AGENTS: 'parent_agents',
  AGENTS_LIST: 'agents_list',
  MICRO_AGENTS: 'micro_agents',
  MINI_AGENTS: 'mini_agents'
};

// Export TTL constants
export const CACHE_TTL = {
  SHORT: 30 * 1000,          // 30 seconds (for status data)
  MEDIUM: 5 * 60 * 1000,     // 5 minutes (for lists)
  LONG: 15 * 60 * 1000,      // 15 minutes
  VERY_LONG: 60 * 60 * 1000  // 1 hour
};

// Setup periodic cache cleanup (every 5 minutes)
setInterval(() => {
  cacheService.cleanup();
}, 5 * 60 * 1000);

export default cacheService;